/**
 * FilterGroupCardMolecule
 *
 * Renders a FilterGroup as ONE atomic card — never flattened into rows (dev
 * note #1). Flattening (A OR B) AND C into three chips misstates the logic and
 * is visually ambiguous in a 372px popover; instead we summarise the expression
 * and offer a "View" link to open the full advanced editor.
 *
 * Provenance variants: context (locked, no copy), view (locked unless _own),
 * user (editable). The card always offers a forward action (View or Copy to my
 * own) so a read-only user is never stuck (dev note #3, #4).
 */
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'localization';
import type { FilterGroup } from 'types/filters';
import type { PropertyOption } from '../PropertyFilter';
import type { PerspectiveOption } from '../PerspectiveFilter';
import type { RelationshipOption } from '../RelationshipFilter';
import type { FilterLayer } from 'types/contextView';
import { cfGroupStats, readsAsPredicate } from '../../../utils/cfGroup';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import './FilterGroupCardMolecule.scss';

export interface FilterGroupCardMoleculeProps {
  group: FilterGroup;
  /** Which config layer this group belongs to — drives badge colour and locked state. */
  provenance: Extract<FilterLayer, 'context' | 'view' | 'user'>;
  /** When true the card shows a View (eye) action instead of Edit. */
  locked: boolean;
  /** Called when the user clicks "View" to open the full advanced editor. */
  onView?: () => void;
  /**
   * Called when the user clicks "Edit" — the `!locked` counterpart to
   * `onView`. Supplying this (with `locked={false}`) switches the header
   * from the read-only View action to Edit + (optionally) Remove, matching
   * the prototype's `CFGroupCard` `canEdit` branch (`context/column-filter.jsx:120-136`)
   * — a group the caller can save to directly, not just open read-only.
   */
  onEdit?: () => void;
  /**
   * Called once the user CONFIRMS removal — only offered alongside `onEdit`.
   * The remove ("x") button first opens an inline confirm stating what is
   * removed, who it affects and that it cannot be undone (ux-craft I4); the
   * removal is saved straight to the group's layer, so there is no undo.
   */
  onRemove?: () => void;
  /**
   * Display name of the view a `provenance="view"` group is saved in — named
   * in the remove confirmation so the user knows whose view they are changing.
   */
  viewName?: string;
  /** Called when the user clicks "Copy to my own" — branches the group into
   *  a session-tier copy tagged `_own: true` (dev note #4). */
  onCopyToOwn?: () => void;
  /** Human-readable reason the group is locked (shown in the card footer). */
  lockedReason?: string;
  /** The aspect catalog — the summary names properties and values by their labels. */
  availableProperties?: PropertyOption[];
  availableRelationships?: RelationshipOption[];
  availablePerspectives?: PerspectiveOption[];
  className?: string;
  /** Parity instrumentation only — the provenance badge (`uf-column.pill`/`.pill#3`). */
  pillDatumId?: string;
  /** Parity instrumentation only — the read-only "View" action (`uf-column.view`/`.view#2`). */
  viewDatumId?: string;
  /** Parity instrumentation only — the editable "Edit" action (`uf-column.edit`/`.edit#2`). */
  editDatumId?: string;
  /** Parity instrumentation only — the editable "Remove this group" action (`uf-column.remove-this-group`/`#2`). */
  removeDatumId?: string;
  /** Parity instrumentation only — the "Copy to my own" action (`uf-column.copy-to-my-own`/`#2`). */
  copyToOwnDatumId?: string;
}

const BADGE_LABEL_KEY: Record<string, string> = {
  context: 'columnFilters.group.badge.context',
  view: 'columnFilters.group.badge.view',
  user: 'columnFilters.group.badge.user',
};

const BADGE_ICON: Record<string, string> = {
  context: 'pi-lock',
  view: 'pi-bookmark',
  user: 'pi-user',
};

const FilterGroupCardMolecule: React.FC<FilterGroupCardMoleculeProps> = ({
  group,
  provenance,
  locked,
  onView,
  onEdit,
  onRemove,
  viewName,
  onCopyToOwn,
  lockedReason,
  availableProperties,
  availableRelationships,
  availablePerspectives,
  className = '',
  pillDatumId,
  viewDatumId,
  editDatumId,
  removeDatumId,
  copyToOwnDatumId,
}) => {
  const { t, i18n } = useTranslation();
  const stats = cfGroupStats(group);
  // The same reader's-words sentence the tree's "Reads as" footer uses.
  const expression = readsAsPredicate(group, {
    t,
    availableProperties,
    availableRelationships,
    availablePerspectives,
    locale: i18n?.language,
  });

  // Inline remove confirmation (ux-craft I4). Focus moves into the confirm when
  // it opens and back to the remove button when it is dismissed (ux-craft A1).
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  const removeButtonRef = useRef<HTMLButtonElement>(null);
  const confirmRef = useRef<HTMLFieldSetElement>(null);
  const restoreFocusRef = useRef(false);
  const confirmMessageId = useId();

  useEffect(() => {
    if (confirmingRemove) {
      // Cancel is the confirm's first button — the safe default for a destructive prompt.
      confirmRef.current?.querySelector<HTMLButtonElement>('button')?.focus();
    } else if (restoreFocusRef.current) {
      restoreFocusRef.current = false;
      removeButtonRef.current?.focus();
    }
  }, [confirmingRemove]);

  const cancelRemove = useCallback(() => {
    restoreFocusRef.current = true;
    setConfirmingRemove(false);
  }, []);

  const confirmRemove = useCallback(() => {
    setConfirmingRemove(false);
    onRemove?.();
  }, [onRemove]);

  let removeConfirmMessage: string;
  if (provenance !== 'view') {
    removeConfirmMessage = t('columnFilters.group.removeConfirm.user');
  } else if (viewName) {
    removeConfirmMessage = t('columnFilters.group.removeConfirm.view', { viewName });
  } else {
    removeConfirmMessage = t('columnFilters.group.removeConfirm.viewUnnamed');
  }

  return (
    <div
      className={`filter-group-card filter-group-card--${provenance} ${className}`.trim()}
      // One card per group of its tier — the prototype's `each(contextFilters)` /
      // `each(viewFilters)` collection, which PAR-5 requires the render to declare.
      data-container={`each(${provenance}Filters)`}
    >
      {/* ── Header ───────────────────────────────────────────────────────── */}
      <div className="filter-group-card__header">
        <i className="pi pi-sitemap filter-group-card__group-icon" aria-hidden="true" />
        <span className="filter-group-card__group-label">
          {t('columnFilters.group.label')}
        </span>
        <span className="filter-group-card__stats">
          {t('columnFilters.group.stats', {
            conditions: stats.conditions,
            nested: stats.nested,
          })}
        </span>

        <span className="filter-group-card__spacer" />

        {/* Provenance badge */}
        <span
          className={`filter-group-card__badge filter-group-card__badge--${provenance}`}
          data-datum={pillDatumId}
          data-kind={pillDatumId ? 'pill' : undefined}
        >
          <i className={`pi ${BADGE_ICON[provenance]}`} aria-hidden="true" />
          {t(BADGE_LABEL_KEY[provenance])}
        </span>

        {/* View action — read-only groups (`locked`), same handler shape as
            `onEdit` but rendered "View" (`context/column-filter.jsx:113-118`). */}
        {locked && onView && (
          <button
            type="button"
            className="filter-group-card__view-btn"
            onClick={onView}
            aria-label={t('columnFilters.group.view')}
            title={t('columnFilters.group.viewTitle')}
            data-datum={viewDatumId}
            data-kind={viewDatumId ? 'button' : undefined}
          >
            <i className="pi pi-eye" aria-hidden="true" />
            {t('columnFilters.group.view')}
          </button>
        )}

        {/* Edit + Remove — an editable group (`!locked`), matching the
            prototype's `canEdit` branch (`context/column-filter.jsx:120-131`).
            `onRemove` is optional: a caller may allow editing a group in place
            without offering to delete it. */}
        {!locked && onEdit && (
          <button
            type="button"
            className="filter-group-card__view-btn"
            onClick={onEdit}
            aria-label={t('columnFilters.group.edit')}
            title={t('columnFilters.group.editTitle')}
            data-datum={editDatumId}
            data-kind={editDatumId ? 'button' : undefined}
          >
            <i className="pi pi-pencil" aria-hidden="true" />
            {t('columnFilters.group.edit')}
          </button>
        )}
        {!locked && onEdit && onRemove && (
          <button
            ref={removeButtonRef}
            type="button"
            className="filter-group-card__remove-btn"
            onClick={() => setConfirmingRemove(true)}
            aria-expanded={confirmingRemove}
            aria-label={t('columnFilters.group.remove')}
            title={t('columnFilters.group.remove')}
            data-datum={removeDatumId}
            data-kind={removeDatumId ? 'button' : undefined}
          >
            <i className="pi pi-times" aria-hidden="true" />
          </button>
        )}

        {locked && <i className="pi pi-lock filter-group-card__lock" aria-hidden="true" />}
      </div>

      {/* ── Expression body (dev notes #1, #2) ───────────────────────────── */}
      <div className="filter-group-card__expression" aria-label={t('columnFilters.group.expressionLabel')}>
        {expression}
      </div>

      {/* ── Remove confirmation (ux-craft I4) — states what is removed, who
          it affects and that it cannot be undone. ─────────────────────────── */}
      {confirmingRemove && (
        <fieldset
          className="filter-group-card__remove-confirm filter-group-card__footer"
          ref={confirmRef}
          aria-labelledby={confirmMessageId}
          data-testid="filter-group-card-remove-confirm"
        >
          <span id={confirmMessageId} className="filter-group-card__locked-reason">
            {removeConfirmMessage}
          </span>
          <ButtonAtom
            type="button"
            variant="text"
            size="small"
            label={t('common.actions.cancel')}
            onClick={cancelRemove}
          />
          <ButtonAtom
            type="button"
            variant="danger"
            size="small"
            label={t('columnFilters.group.removeConfirm.action')}
            onClick={confirmRemove}
          />
        </fieldset>
      )}

      {/* ── Footer: locked reason + copy action (dev note #4) ────────────── */}
      <div className="filter-group-card__footer">
        {lockedReason && (
          <span className="filter-group-card__locked-reason">{lockedReason}</span>
        )}
        {onCopyToOwn && (
          <button
            type="button"
            className="filter-group-card__copy-btn"
            onClick={onCopyToOwn}
            data-datum={copyToOwnDatumId}
            data-kind={copyToOwnDatumId ? 'button' : undefined}
          >
            <i className="pi pi-copy" aria-hidden="true" />
            {t('columnFilters.group.copyToMyOwn')}
          </button>
        )}
      </div>
    </div>
  );
};

export default FilterGroupCardMolecule;
